import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import productImgPink from "../assets/productImg.png";
import productImgBlack from "../assets/productImgBlack.png";

gsap.registerPlugin(ScrollTrigger);

const colors = [
  { label: "Blush Pink", img: productImgPink },
  { label: "Midnight Black", img: productImgBlack },
];

const OrderSection = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const imgRef = useRef(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        imgRef.current,
        { opacity: 0, scale: 0.9, y: 30 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 72%" },
        },
      );
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          delay: 0.15,
          scrollTrigger: { trigger: sectionRef.current, start: "top 72%" },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSelect = (i) => {
    gsap.fromTo(
      imgRef.current,
      { opacity: 0, scale: 0.93 },
      { opacity: 1, scale: 1, duration: 0.35, ease: "power2.out" },
    );
    setSelected(i);
  };

  return (
    <Section ref={sectionRef} id="order">
      <BgText>YOURS</BgText>
      <Inner>
        <ImageSide ref={imgRef}>
          <img
            src={colors[selected].img}
            alt={colors[selected].label}
            draggable="false"
          />
          <OrderGlow />
        </ImageSide>

        <ContentSide ref={contentRef}>
          <Eyebrow>Limited Launch — Kosovo Exclusive</Eyebrow>
          <Title>
            Make it
            <br />
            <em>yours today.</em>
          </Title>
          <Description>
            Drejtuesi i flokëve portativ vjen për herë të parë në Kosovë me
            super cmim. Nuk ka më nevojë për sallone bukurie as për drejtues
            ordiner që përdoren cdo ditë.
          </Description>

          <PriceWrap>
            <Price>€19.99</Price>
            <PriceNote>I pari në Kosovë</PriceNote>
          </PriceWrap>

          <ColorLabel>Zgjidhni ngjyrën</ColorLabel>
          <ColorOptions>
            {colors.map((c, i) => (
              <ColorBtn
                key={i}
                $active={selected === i}
                $black={i === 1}
                onClick={() => handleSelect(i)}
              >
                <ColorSwatch $black={i === 1} $active={selected === i} />
                {c.label}
              </ColorBtn>
            ))}
          </ColorOptions>

          <Perks>
            <Perk>✓ Vjen me USB-C Kabllo</Perk>
            <Perk>✓ Transport ne Kosovë, Shqipëri dhe Macedoni</Perk>
            <Perk>✓ 2 ngjyra në dispozicion</Perk>
            <Perk>✓ 8×8×3 cm</Perk>
          </Perks>

          <BuyBtn href="/checkout">Porosit Tani →</BuyBtn>
          <Sub>Pyetje? Na kontaktoni ne Instagram @siarabeautyks</Sub>
        </ContentSide>
      </Inner>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: linear-gradient(160deg, #fdf8f5 0%, #fceee8 60%, #f7e0d5 100%);
  padding: 5rem 3rem;
  overflow: hidden;
  z-index: 999999;

  @media (max-width: 768px) {
    padding: 4rem 1.4rem;
  }
`;

const BgText = styled.div`
  position: absolute;
  bottom: -3rem;
  right: -1rem;
  font-family: var(--serif);
  font-size: clamp(7rem, 16vw, 16rem);
  font-weight: 300;
  color: rgba(212, 167, 154, 0.08);
  pointer-events: none;
  user-select: none;
  line-height: 1;
`;

const Inner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 4rem;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 2.5rem;
    text-align: center;
  }
`;

const ImageSide = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    max-width: 280px;
    object-fit: contain;
    filter: drop-shadow(0 32px 64px rgba(180, 120, 100, 0.3));
    position: relative;
    z-index: 1;
  }
`;

const OrderGlow = styled.div`
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(232, 197, 184, 0.6) 0%,
    transparent 70%
  );
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ContentSide = styled.div`
  flex: 1.2;
  max-width: 440px;
`;

const Eyebrow = styled.p`
  font-family: var(--sans);
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: var(--serif);
  font-size: clamp(2.8rem, 4.5vw, 4.4rem);
  font-weight: 300;
  line-height: 1.12;
  color: var(--dark);
  margin-bottom: 1.1rem;

  em {
    font-style: italic;
    color: var(--gold);
  }
`;

const Description = styled.p`
  font-family: var(--sans);
  font-size: 0.9rem;
  font-weight: 300;
  line-height: 1.75;
  color: var(--mid);
  margin-bottom: 1.8rem;
`;

const PriceWrap = styled.div`
  margin-bottom: 1.6rem;
`;

const Price = styled.div`
  font-family: var(--serif);
  font-size: clamp(2.2rem, 3.5vw, 3.2rem);
  font-weight: 400;
  color: var(--dark);
  line-height: 1;
  margin-bottom: 0.35rem;
`;

const PriceNote = styled.p`
  font-family: var(--sans);
  font-size: 0.78rem;
  font-weight: 300;
  color: var(--gold);
  letter-spacing: 0.05em;
`;

const ColorLabel = styled.p`
  font-family: var(--sans);
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--mid);
  margin-bottom: 0.8rem;
`;

const ColorOptions = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-bottom: 1.8rem;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const ColorBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--sans);
  font-size: 0.78rem;
  font-weight: ${({ $active }) => ($active ? "500" : "300")};
  color: ${({ $active }) => ($active ? "var(--dark)" : "var(--mid)")};
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.7)" : "transparent"};
  border: 1px solid
    ${({ $active }) => ($active ? "var(--dusty)" : "var(--rose)")};
  border-radius: 100px;
  padding: 0.5rem 1.1rem 0.5rem 0.6rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--dusty);
    color: var(--dark);
  }
`;

const ColorSwatch = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $black }) => ($black ? "#1a1a1a" : "#f2d0c8")};
  border: 1px solid
    ${({ $black }) => ($black ? "transparent" : "rgba(180,120,100,0.2)")};
  flex-shrink: 0;
`;

const Perks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;

  @media (max-width: 900px) {
    align-items: center;
  }
`;

const Perk = styled.p`
  font-family: var(--sans);
  font-size: 0.82rem;
  font-weight: 300;
  color: var(--mid);
  letter-spacing: 0.03em;
`;

const BuyBtn = styled.a`
  display: inline-block;
  font-family: var(--sans);
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--cream);
  background: var(--dark);
  padding: 1rem 2.8rem;
  border-radius: 100px;
  margin-bottom: 0.9rem;
  transition:
    background 0.3s ease,
    transform 0.25s ease;

  &:hover {
    background: var(--gold);
    transform: translateY(-2px);
  }
`;

const Sub = styled.p`
  font-family: var(--sans);
  font-size: 0.72rem;
  font-weight: 300;
  color: var(--dusty);
  letter-spacing: 0.05em;
`;

export default OrderSection;
